import axios from "axios"
import { JobListingPage, JobListingListResponse } from "src/types/jobs"

const JOB_PAGE_TYPE = "jobs.JobListingPage"
const REVALIDATE_INTERVAL = 60 * 10

function getContentBaseUrl() {
  return process.env.NEXT_PUBLIC_CONTENT_URL?.replace(/\/$/, "") || ""
}

function createJobsEndpoint() {
  const baseUrl = getContentBaseUrl()

  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_CONTENT_URL is not configured")
  }

  return `${baseUrl}/api/v2/pages/`
}

function isPublishedJob(job: Pick<JobListingPage, "status">) {
  return job.status === "active"
}

export async function fetchJobListings({ includeInactive = false }: { includeInactive?: boolean } = {}) {
  console.log(`${createJobsEndpoint()}?type=${JOB_PAGE_TYPE}&fields=*`)
  const { data } = await axios.get<JobListingListResponse>(`${createJobsEndpoint()}?type=${JOB_PAGE_TYPE}&fields=*`)

  const items = includeInactive ? data.items : data.items.filter(isPublishedJob)

  return {
    ...data,
    items,
    meta: {
      ...data.meta,
      total_count: items.length,
    },
  }
}

export async function fetchJobListingBySlug(
  slug: string,
  { includeInactive = false }: { includeInactive?: boolean } = {}
) {
  const jobListings = await fetchJobListings({ includeInactive: true })
  const listing = jobListings.items.find((job) => job.meta.slug === slug)

  if (!listing || (!includeInactive && !isPublishedJob(listing))) {
    return null
  }

  const { data } = await axios.get<JobListingPage>(`${createJobsEndpoint()}${listing.id}/?fields=*`)

  if (!includeInactive && !isPublishedJob(data)) {
    return null
  }

  return data
}

export function getJobListingHref(slug: string) {
  return `/komm-ins-team/${slug}`
}

export function getJobsPageHref() {
  return "/komm-ins-team"
}

export function getJobEmploymentTypeLabel(value: string | null | undefined, locale = "de") {
  if (!value) {
    return null
  }

  const labels = {
    de: {
      full_time: "Vollzeit",
      part_time: "Teilzeit",
      contract: "Vertrag",
      internship: "Praktikum",
      temporary: "Zeitarbeit",
    },
    en: {
      full_time: "Full-time",
      part_time: "Part-time",
      contract: "Contract",
      internship: "Internship",
      temporary: "Temporary",
    },
  }

  return labels[locale]?.[value] || value.replace(/_/g, " ")
}

export function getJobWorkModeLabel(value: string | null | undefined, locale = "de") {
  if (!value) {
    return null
  }

  const labels = {
    de: {
      remote: "Remote",
      hybrid: "Hybrid",
      on_site: "Vor Ort",
    },
    en: {
      remote: "Remote",
      hybrid: "Hybrid",
      on_site: "On-site",
    },
  }

  return labels[locale]?.[value] || value.replace(/_/g, " ")
}

export function getJobSalaryPeriodLabel(value: string | null | undefined, locale = "de") {
  if (!value) {
    return null
  }

  const labels = {
    de: {
      hourly: "pro Stunde",
      daily: "pro Tag",
      weekly: "pro Woche",
      monthly: "pro Monat",
      yearly: "pro Jahr",
    },
    en: {
      hourly: "per hour",
      daily: "per day",
      weekly: "per week",
      monthly: "per month",
      yearly: "per year",
    },
  }

  return labels[locale]?.[value] || value.replace(/_/g, " ")
}

export function formatJobSalary(job: Pick<JobListingPage, "salary_hidden" | "salary_min" | "salary_max" | "salary_currency" | "salary_period">, locale = "de") {
  if (job.salary_hidden || (!job.salary_min && !job.salary_max) || !job.salary_currency) {
    return null
  }

  const formatter = new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-US", {
    style: "currency",
    currency: job.salary_currency,
    maximumFractionDigits: 0,
  })

  const min = typeof job.salary_min === "number" ? formatter.format(job.salary_min) : null
  const max = typeof job.salary_max === "number" ? formatter.format(job.salary_max) : null
  const period = getJobSalaryPeriodLabel(job.salary_period, locale)

  if (min && max) {
    return `${min} – ${max}${period ? ` ${period}` : ""}`
  }

  return `${min || max}${period ? ` ${period}` : ""}`
}

export function formatJobDeadline(date: string | null | undefined, locale = "de") {
  if (!date) {
    return null
  }

  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", {
    dateStyle: "long",
  }).format(new Date(date))
}

export function hasRichTextContent(value: string | null | undefined) {
  return Boolean(value && value.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim())
}

export function shadeColor(color, percent) {
  var R = parseInt(color.substring(1, 3), 16)
  var G = parseInt(color.substring(3, 5), 16)
  var B = parseInt(color.substring(5, 7), 16)

  R = parseInt(String((R * (100 + percent)) / 100))
  G = parseInt(String((G * (100 + percent)) / 100))
  B = parseInt(String((B * (100 + percent)) / 100))

  R = R < 255 ? R : 255
  G = G < 255 ? G : 255
  B = B < 255 ? B : 255

  R = Math.round(R)
  G = Math.round(G)
  B = Math.round(B)

  var RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16)
  var GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16)
  var BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16)

  return "#" + RR + GG + BB
}


export { REVALIDATE_INTERVAL }

